import { logWarning } from '@databases/logs';
import { DatabaseFieldType } from '@databases/types/database';
import { DatabaseViewSettings } from '@databases/types/database/view';
import {
  DatabaseChange,
  DatabaseChangeType,
  addChangeToChangeset,
  DatabaseDraft,
  ValueChange,
} from '@databases/utils/database/changesets';
import { convertType, getInitialValue } from '@databases/utils/database/field-types';
import {
  ImportErrorTag,
  ImportSettings,
  resolveFieldReference,
  resolveEntryReference,
  ImportColumn,
  ImportResult,
  ImportFieldMode,
  ImportEntryMode,
  ImportEntryReference,
  isImportedContentEmpty,
  getImportSettingsFromSelection,
  importFileStep1,
  importFileStep2,
  importFileStep3,
  getImportSettingsWithMissingItems,
} from '@databases/utils/database/import';
import { getOrApplyValueChange } from '@databases/utils/deltas/paste-value-delta';
import { collabDatabaseBatch } from '@databases/utils/redux/collab/batch';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { batch, SimpleStore } from '@databases/utils/redux/redux';
import { AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';

import {
  selectHasSelectedCells,
  selectSelectedEntireEntryIds,
  selectSelectedEntireFieldIds,
  selectSelectedEntryIds,
  selectSelectedFieldIds,
} from '../cellSelections/selectors';
import { setDraftedChangeset } from '../changesets/actions';
import {
  selectDatabaseDraftByContentId,
  selectDraftedChangeset,
} from '../changesets/selectors';
import { DefaultRootState } from '../redux';

type Dispatcher<Result> = ThunkAction<Result, DefaultRootState, void, AnyAction>;

/**
 * Returns the database changes mapped from the value changes of the pasted content.
 * Coverts the pasted cell text value to the new field type if the cell value type is different from the field type.
 **/
export function getValueChanges(
  cell: ImportColumn,
  settings: ImportSettings,
  entryIdx: number,
  fieldIdx: number,
  database: DatabaseDraft,
  store: SimpleStore<DefaultRootState>
): DatabaseChange[] {
  const entryRef = settings.entryReferences[entryIdx];
  const entry = resolveEntryReference(database, entryRef);
  const fieldRef = settings.fieldReferences[fieldIdx];
  const field = resolveFieldReference(database, fieldRef);
  const isSingleCellSelected =
    settings.selectedRows.length === 1 && settings.selectedColumns.length === 1;

  if (!field || !entry) {
    return [];
  }

  const convertedValue =
    cell?.text && cell.value?.type !== field.type
      ? convertType(
          {
            database,
            field,
            value: {
              value: cell.text,
              type: DatabaseFieldType.TEXT,
            },
          },
          field.type,
          store.getState()
        )
      : cell.value!;

  const valueChange = getOrApplyValueChange(
    {
      database,
      field,
      entry,
      value: convertedValue,
      text: cell.text,
    },
    store,
    isSingleCellSelected
  );

  return (valueChange || []).map((change: ValueChange) => ({
    type: DatabaseChangeType.SET_VALUE,
    entryId: entry.id,
    value: convertedValue,
    fieldId: field.id,
    change,
  }));
}

/**
 * Returns the database change for adding a new entry with the pasted content as text values
 * */
const handleAddEntryChange = (
  entryIdx: number,
  databaseDraft: DatabaseDraft,
  settings: ImportSettings,
  importResult: ImportResult,
  entryRef: ImportEntryReference
): DatabaseChange => {
  const entry = resolveEntryReference(databaseDraft, entryRef);
  return {
    type: DatabaseChangeType.ADD_ENTRY,
    entry: {
      ...entry,
      values: Object.fromEntries(
        settings.fieldReferences
          .map((fieldRef, idx) => {
            const field = resolveFieldReference(databaseDraft, fieldRef);
            const cell = importResult.rows?.[entryIdx]?.cols?.[idx];
            const value = cell?.value || {
              value: cell?.text || getInitialValue(DatabaseFieldType.TEXT).value,
              type: DatabaseFieldType.TEXT,
            };
            return field?.id && value ? [field.id, value] : [];
          })
          .filter(([fieldId, value]) => fieldId && value)
      ),
    },
  } as DatabaseChange;
};

/**
 *  Iterates over the fields and creates a change for replacing the value of the selected cells with the pasted content.
 * */
const handleFieldChanges = (
  entryIdx: number,
  recurringEntryIdx: number,
  databaseDraft: DatabaseDraft,
  settings: ImportSettings,
  importResult: ImportResult,
  store: SimpleStore<DefaultRootState>,
  currentViewId: string | undefined
): DatabaseChange[] => {
  let changes: DatabaseChange[] = [];
  let recurringFieldIdx = 0;
  settings.fieldReferences.forEach((fieldRef, fieldIdx) => {
    const field = resolveFieldReference(databaseDraft, fieldRef);
    const shouldCreateField = fieldRef?.type === ImportFieldMode.CREATE;
    const isColSelected = settings.selectedColumns[fieldIdx];
    const isRowSelected = settings.selectedRows[entryIdx];
    const cell = importResult.rows?.[entryIdx]?.cols?.[fieldIdx];
    const recurringCell =
      importResult.rows?.[recurringEntryIdx]?.cols?.[recurringFieldIdx];
    const importCell =
      cell || (isRowSelected && isColSelected ? recurringCell : undefined);

    if (shouldCreateField && field && importCell) {
      const createChange: DatabaseChange = {
        type: DatabaseChangeType.ADD_FIELD,
        field,
        currentViewId,
      };
      changes.push(createChange);
    }
    if (importCell) {
      const fieldChanges = getValueChanges(
        importCell,
        settings,
        entryIdx,
        fieldIdx,
        databaseDraft,
        store
      );
      changes = [...changes, ...fieldChanges];
    }
    recurringFieldIdx++;
    if (importResult.rows?.[recurringEntryIdx]?.cols?.length === recurringFieldIdx) {
      recurringFieldIdx = 0;
    }
  });
  return changes;
};

/**
 * Iterates over the entries and creates a change for replacing the value of the selected cells with the pasted content.
 * If the pasted content area is larger than the selected area, the remaining cells will be filled with the pasted content.
 * If the selected area is larger than the pasted content, the cell will be filled with a recurring sequence of the pasted content.
 * */
export const createRangedChanges = (
  settings: ImportSettings,
  importResult: ImportResult,
  databaseDraft: DatabaseDraft,
  store: SimpleStore<DefaultRootState>,
  currentViewId: string | undefined
) => {
  let changes: DatabaseChange[] = [];

  let recurringEntryIdx = 0;
  settings.entryReferences.forEach((entryRef, entryIdx) => {
    if (entryRef?.type === ImportEntryMode.CREATE) {
      const addEntryChange = handleAddEntryChange(
        entryIdx,
        databaseDraft,
        settings,
        importResult,
        entryRef
      );
      changes = [...changes, addEntryChange];
    } else {
      const fieldChanges = handleFieldChanges(
        entryIdx,
        recurringEntryIdx,
        databaseDraft,
        settings,
        importResult,
        store,
        currentViewId
      );
      changes = [...changes, ...fieldChanges];
    }
    recurringEntryIdx++;
    if (importResult.rows?.length === recurringEntryIdx) {
      recurringEntryIdx = 0;
    }
  });

  return changes;
};

/**
 * Saves the pasted content to the database draft.
 **/
export const doSavePastedContent =
  (
    contentId: string,
    parentScope: string,
    scope: string,
    parsedFile: ImportResult,
    settings: ImportSettings,
    currentViewId: string | undefined
  ): Dispatcher<void | { tag: ImportErrorTag.UNKNOWN_FILE_TYPE; fileType: string }> =>
  (dispatch, getState) => {
    const databaseDraft = selectDatabaseDraftByContentId(getState(), {
      contentId,
      scope,
      includeDeleted: true,
    });

    const changes = createRangedChanges(
      settings,
      parsedFile,
      databaseDraft,
      {
        dispatch,
        getState,
      },
      currentViewId
    );

    let createFieldsChangeSet = selectDraftedChangeset(getState(), { contentId, scope });
    createFieldsChangeSet = addChangeToChangeset(
      createFieldsChangeSet,
      ...changes.filter((change) => change.type === DatabaseChangeType.ADD_FIELD)
    );

    let setValuesChangeset = selectDraftedChangeset(getState(), { contentId, scope });
    setValuesChangeset = addChangeToChangeset(
      setValuesChangeset,
      ...changes.filter((change) => change.type !== DatabaseChangeType.ADD_FIELD)
    );

    // Prevent to save this changesets in one collabDatabaseBatch because setting values depend on creating fields before
    batch(() => {
      collabDatabaseBatch({ dispatch, getState }, contentId, () => {
        dispatch(setDraftedChangeset(contentId, parentScope, createFieldsChangeSet));
      });
      collabDatabaseBatch({ dispatch, getState }, contentId, () => {
        dispatch(setDraftedChangeset(contentId, parentScope, setValuesChangeset));
      });
    });
  };

export const doImportContent =
  (
    contentId: string,
    files: DataTransferItemList | File[],
    scope: string,
    viewSettings: DatabaseViewSettings | undefined,
    currentViewId: string,
    defaultFieldLabel: string
  ): Dispatcher<Promise<void>> =>
  async (dispatch, getState) => {
    if (isImportedContentEmpty(files)) {
      return;
    }

    const state = getState();
    const database = selectDatabaseByContentIdRequired(state, {
      contentId,
    });
    const isSomeSelected = selectHasSelectedCells(state, {
      contentId,
      scope,
      viewSettings,
    });

    if (isSomeSelected) {
      const selectedEntireFields = selectSelectedEntireFieldIds(state, {
        contentId,
        scope,
        viewSettings,
      });
      const selectedEntireEntries = selectSelectedEntireEntryIds(state, {
        contentId,
        scope,
        viewSettings,
      });

      const selectedFieldIds = selectedEntireEntries.length
        ? database.fields
        : selectSelectedFieldIds(state, {
            contentId,
            scope,
            viewSettings,
          });
      const selectedEntryIds = selectedEntireFields.length
        ? database.entries
        : selectSelectedEntryIds(state, {
            contentId,
            scope,
            viewSettings,
          });

      const fieldIds = database.fields;
      const entryIds = database.entries;
      const settings = getImportSettingsFromSelection(
        selectedFieldIds,
        selectedEntryIds,
        fieldIds,
        entryIds
      );
      const fileAsText = await importFileStep1(files);
      if (!fileAsText.success) {
        switch (fileAsText.error.tag) {
          case ImportErrorTag.UNSUPPORTED_MIME_TYPE:
            logWarning(new Error(`Unsupported mime type ${fileAsText.error.fileType}.`));
            return;
          case ImportErrorTag.PASTED_FILE_CANNOT_BE_READ:
            logWarning(new Error('Pasted file cannot be read.'));
            return;
          case ImportErrorTag.UNEXPECTED_DATA_TYPE:
            logWarning(new Error('Unexpected data type.'));
            return;
          default:
            logWarning(new Error(`Unhandled import error: ${fileAsText.error}`));
            return;
        }
      }

      const fileContent = await importFileStep2(fileAsText.file);
      if (!fileContent.success) {
        switch (fileContent.error.tag) {
          case ImportErrorTag.TABLE_COULD_NOT_BE_FOUND_IN_HTML:
            logWarning(new Error('No table could be found in the imported HTML.'));
            return;
          case ImportErrorTag.UNEXPECTED_IMPORT_TYPE:
            logWarning(new Error('Unexpected import file type.'));
            return;
          default:
            logWarning(new Error(`Unhandled parse import error: ${fileContent.error}`));
            return;
        }
      }

      const fileWithRowsAndCols = importFileStep3(
        database,
        fileContent.file,
        settings,
        getState()
      );
      if (!fileWithRowsAndCols.success) {
        logWarning(
          new Error(`Unknown file type: ${fileWithRowsAndCols.error.fileType}.`)
        );
        return;
      }

      const settingsWithMissingItems = getImportSettingsWithMissingItems(
        selectedFieldIds,
        selectedEntryIds,
        fieldIds,
        entryIds,
        database,
        fileWithRowsAndCols.result,
        defaultFieldLabel
      );

      dispatch(
        doSavePastedContent(
          contentId,
          scope,
          scope,
          fileWithRowsAndCols.result,
          settingsWithMissingItems,
          currentViewId
        )
      );
    }
  };
